<template>
    <div class="container-fluid">
        <Loading v-if="!loaded"></Loading>
        <div v-else>
            <!-- TITLE -->
            <div class="mt-2 tabName">
                <h3>{{ $t('logs') }}</h3>
            </div>
            <div class="alert alert-success" v-show="success" role="alert">{{ $t('saved') }} !</div>
            <!-- FILTERS -->
            <div class="form-group row">
                <label class="col-md-2 col-form-label">{{ $t('type') }}</label>
                <div class="col-md-4">
                    <select id="type" class="form-control" v-model="filterParams.searchAction">
                        <option value="all" selected>{{ $t('all') }}</option>
                        <option value="put_in" selected>{{ $t('put_in') }}</option>
                        <option value="take_out" selected>{{ $t('take_out') }}</option>
                        <option value="reservation" selected>{{ $t('reservation') }}</option>
                        <option value="report" selected>{{ $t('report') }}</option>
                        <option value="check" selected>{{ $t('inventory_check') }}</option>
                        <option value="change" selected>{{ $t('change') }}</option>
                        <option value="full" selected>{{ $t('full') }}</option>
                        <option value="alarm" selected>{{ $t('alarm') }}</option>
                    </select>
                </div>
                <label class="col-md-2 col-form-label">{{ $t('handler') }}</label>
                <div class="col-md-4">
                    <select id="search_portal_partner" class="form-control select" v-model="filterParams.searchUser">
                        <option value="all" selected="selected">{{ $t('all') }}</option>
                        <option
                            v-for="drugSafeUser in drugSafeUsers"
                            v-bind:value="drugSafeUser.id"
                            v-bind:key="drugSafeUser.id"
                        >
                            {{ drugSafeUser.first_name }} {{ drugSafeUser.last_name }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="form-group row">
                <label class="col-md-2 col-form-label">{{ $t('from') }}</label>
                <div class="col-md-4">
                    <input type="date" class="form-control" v-model="filterParams.searchFrom" />
                </div>
                <label class="col-md-2 col-form-label">{{ $t('to') }}</label>
                <div class="col-md-4">
                    <input type="date" class="form-control" v-model="filterParams.searchTo" />
                </div>
            </div>
            <div class="form-group row">
                <label class="col-md-2 col-form-label">{{ $t('name') }} {{ $t('or') }} {{ $t('vnr') }}</label>
                <div class="col-md-4">
                    <input type="text" class="form-control" v-model="filterParams.searchName" />
                </div>
            </div>
            <!-- BUTTONS -->
            <div class="form-group row mt-4">
                <div class="d-flex ml-3 mr-5">
                    <button class="btn btn-primary" v-on:click="getLogs" :disabled="submitClicked">
                        {{ $t('search') }}
                    </button>
                </div>
                <div class="d-flex mr-5">
                    <button class="btn btn-secondary" v-on:click="resetFilters" :disabled="submitClicked">
                        {{ $t('reset') }}
                    </button>
                </div>
            </div>
            <!-- TABLE -->
            <b-table class="mt-4" striped hover :fields="tableFields" :items="logs" sort-by="created_at" sort-desc>
                <template #head(showAmount)="data">
                    <div
                        v-b-tooltip.hover:title="
                            `${$t('packages_pieces_tooltip')}${$t('packages_pieces_tooltip_invcorr')}`
                        "
                    >
                        {{ data.label }}
                    </div>
                </template>
                <template #cell(action)="data">
                    {{ $t(data.item.action) }}
                </template>
                <template #cell(medication_name)="data">
                    {{ data.item.medication_name }} {{ data.item.company }} {{ data.item.dosage }}
                    {{ data.item.package_size }} {{ data.item.form }} {{ data.item.dosage_form }}
                </template>
                <template #cell(showAmount)="data">
                    {{ data.item.showAmount !== '' ? getDisplayAmount(data.item) : '' }}
                </template>
                <template #cell(locker)="data">
                    {{
                        data.item.locker
                            .split(',')
                            .sort()
                            .join(', ')
                    }}
                </template>
                <template #cell(created_at)="data">
                    {{ getLocalDate(data.item.created_at, $i18n.locale, true, true) }}
                </template>
                <template #cell(button_show)="data">
                    <button
                        class="btn btn-sm btn-success ml-sm-0"
                        v-on:click="
                            () => $router.push({ name: 'log.show', params: { id: data.item.id, filterParams } })
                        "
                        :disabled="submitClicked"
                    >
                        {{ $t('show') }}
                    </button>
                </template>
                <template #cell(button_edit)="data">
                    <div v-b-tooltip.hover :title="returnCorrectTooltip(data.item)">
                        <button
                            class="btn btn-sm btn-info ml-sm-0"
                            v-on:click="
                                () => $router.push({ name: 'log.edit', params: { id: data.item.id, filterParams } })
                            "
                            :disabled="submitClicked || data.item.signature || data.item.mark_destroy_user"
                        >
                            {{ $t('edit') }}
                        </button>
                    </div>
                </template>
                <template #cell(button_sign)="data">
                    <div v-if="user.role === 'pharmacy_owner' && !data.item.signature">
                        <button
                            class="btn btn-sm btn-info ml-sm-0"
                            v-on:click="() => handleSignLogClick(data.item.id)"
                            :disabled="submitClicked"
                        >
                            {{ $t('sign') }}
                        </button>
                    </div>
                    <div v-if="user.role === 'pharmacy_owner' && data.item.signature">
                        {{ $t('signed') }} {{ data.item.signature.web_user_name }}
                    </div>
                </template>
            </b-table>
        </div>
        <!-- MODAL: ARE YOU SURE YOU WANT TO SIGN LOG? -->
        <b-modal id="signConfirmationModal" centered :title="$t('sign_log_are_you_sure_title')" hide-footer>
            <div class="row">
                <div class="col">
                    <p>{{ $t('sign_log_are_you_sure_text') }}</p>
                </div>
            </div>
            <div class="d-flex row mt-2">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="signLog">
                        {{ $t('ok') }}
                    </button>
                    <button class="btn btn-secondary" @click="handleSignLogCancel">
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import Loading from '@/views/partial/Loading';
import { getDrugSafeUsers, getLogs, getPharmacy, handleAxiosError, signLog } from '@/services/axiosService';
import { dateIsValid, getLocalDate, getLocalDateISOString } from '@/services/dateService';
import { getMedDisplayAmount } from '@/services/medicationService';

export default {
    components: {
        Loading
    },
    data() {
        return {
            active_tab: 'all',
            dateIsValid,
            drugSafeUsers: [],
            error: false,
            filterParams: {
                searchAction: 'all',
                searchFrom: '',
                searchName: '',
                searchTo: '',
                searchUser: 'all'
            },
            fromDate: '',
            getLocalDate,
            initialFromDateOffsetDays: 7,
            loaded: false,
            logs: [],
            pharmacy: null,
            pharmacy_id: null,
            searchName: null,
            signLogId: null,
            submitClicked: false,
            success: false,
            toDate: ''
        };
    },
    async created() {
        // initialize filter params, get pharmacy info, get pharmacy's logs, get pharmacy's drugsafe users
        this.setInitialFilterParams();
        this.pharmacy_id = this.user.pharmacy_id;
        await this.getPharmacy();
        if (!this.error) {
            await this.getLogs(this.filterParams);
        }
        if (!this.error) {
            await this.getUsers();
        }
        this.setLoaded();
    },
    computed: {
        ...mapState('account', ['status', 'user', 'logout']),
        tableFields() {
            return [
                { key: 'action', sortable: true, label: this.$t('type'), tdClass: 'align-middle' },
                { key: 'user_name', sortable: true, label: this.$t('handler'), tdClass: 'align-middle' },
                { key: 'vnr', sortable: true, label: this.$t('vnr'), tdClass: 'align-middle' },
                { key: 'medication_name', sortable: true, label: this.$t('medication_name') },
                {
                    key: 'showAmount',
                    sortable: true,
                    label: `${this.$t('amount')} [${this.$t('packages_pieces_short')}]`,
                    tdClass: 'align-middle'
                },
                { key: 'locker', sortable: true, label: this.$t('locker'), tdClass: 'align-middle' },
                { key: 'created_at', sortable: true, label: this.$t('created_at'), tdClass: 'align-middle' },
                { key: 'button_show', sortable: false, label: '', tdClass: 'align-middle' },
                { key: 'button_edit', sortable: false, label: '', tdClass: 'align-middle' },
                { key: 'button_sign', sortable: false, label: '', tdClass: 'align-middle' }
            ];
        }
    },
    methods: {
        // calculate amount for log (depends on action)
        // used for sorting table by amount column; values not shown!
        calculateShowAmount(entry) {
            let showAmount = '';
            if (
                entry.action === 'change' &&
                entry.amount_changed_packs !== undefined &&
                entry.amount_changed_pills !== undefined
            ) {
                showAmount = entry.amount_changed_packs + entry.amount_changed_pills / entry.package_size;
            } else if (
                entry.action.match(/^(put_in|take_out|reservation)$/) &&
                entry.amount !== undefined &&
                entry.pills_amount !== undefined
            ) {
                showAmount = entry.amount + entry.pills_amount / entry.package_size;
            }
            return showAmount;
        },
        // get displayed amount
        getDisplayAmount(log) {
            let amountPacks = log.amount;
            let amountPills = log.pills_amount;
            let showDirection = false;
            if (
                log.action === 'change' &&
                log.amount_changed_packs !== undefined &&
                log.amount_changed_pills !== undefined
            ) {
                amountPacks = log.amount_changed_packs;
                amountPills = log.amount_changed_pills;
                showDirection = true;
            }
            return getMedDisplayAmount(amountPacks, amountPills, true, null, showDirection);
        },
        // format initial "from" date based on locale and default offset
        getInitialFromDate() {
            const date = new Date(new Date().setDate(new Date().getDate() - this.initialFromDateOffsetDays));
            return getLocalDateISOString(date);
        },
        // get logs
        async getLogs() {
            const params = this.filterParams;
            this.submitClicked = true;
            // add 1 day to searchTo to include current date
            let searchToPlus1 = '';
            if (params.searchTo.length > 0) {
                searchToPlus1 = new Date(params.searchTo);
                searchToPlus1.setDate(new Date(params.searchTo).getDate() + 1);
                searchToPlus1 = searchToPlus1.toISOString().slice(0, 10);
            }

            try {
                let logs = await getLogs(this.pharmacy_id, { params: { ...params, searchTo: searchToPlus1 } });
                this.logs = logs.map(entry => ({
                    ...entry,
                    showAmount: this.calculateShowAmount(entry)
                }));
            } catch (error) {
                this.error = true;
                handleAxiosError(error);
            }
            this.submitClicked = false;
        },
        // get pharmacy info
        async getPharmacy() {
            try {
                this.pharmacy = await getPharmacy(this.pharmacy_id);
            } catch (error) {
                this.error = true;
                handleAxiosError(error);
            }
        },
        // get pharmacy's drugsafe users
        async getUsers() {
            try {
                this.drugSafeUsers = await getDrugSafeUsers(this.pharmacy_id);
            } catch (error) {
                this.error = true;
                handleAxiosError(error);
            }
        },
        // handle sign log cancel action
        handleSignLogCancel() {
            this.submitClicked = false;
            this.signLogId = null;
            this.$bvModal.hide('signConfirmationModal');
        },
        // handle sign log click action
        handleSignLogClick(id) {
            this.submitClicked = true;
            this.signLogId = id;
            this.$bvModal.show('signConfirmationModal'); // show "are you sure?" modal
        },
        // handle "reset" click: reset filters and get logs again
        async resetFilters() {
            this.submitClicked = true;
            this.fromDate = '';
            this.toDate = '';
            this.filterParams.searchAction = 'all';
            this.filterParams.searchUser = 'all';
            this.filterParams.searchFrom = '';
            this.filterParams.searchTo = '';
            this.filterParams.searchName = '';
            await this.getLogs(this.filterParams);
            this.submitClicked = false;
        },
        // set initial filter parameters
        setInitialFilterParams() {
            if (this.$route.params.filterParams) {
                this.filterParams = {
                    ...this.$route.params.filterParams,
                    searchFrom: this.dateIsValid(this.$route.params.filterParams.searchFrom, true)
                        ? this.$route.params.filterParams.searchFrom
                        : this.getInitialFromDate(),
                    searchTo: this.dateIsValid(this.$route.params.filterParams.searchTo, true)
                        ? this.$route.params.filterParams.searchTo
                        : ''
                };
            } else {
                this.filterParams.searchFrom = this.getInitialFromDate();
            }
        },
        // set "page loaded" value
        setLoaded() {
            if (!this.error) {
                this.loaded = true;
            }
        },
        // sign log
        async signLog() {
            try {
                this.$bvModal.hide('signConfirmationModal');
                const signData = await signLog(this.pharmacy_id, { logId: this.signLogId });
                this.$toastr.s(this.$t('log_signed'));
                this.logs = this.logs.map(log => (log.id === this.signLogId ? { ...log, ...signData } : log)); // update signed log listing
            } catch (error) {
                handleAxiosError(error);
            }
            this.handleSignLogCancel();
        },
        returnCorrectTooltip(item) {
            if (item.signature && item.mark_destroy_user){
                return this.$t('error_cannot_edit_signed_and_marked_log')
            } else if (item.signature && !item.mark_destroy_user) {
                return this.$t('error_cannot_edit_signed_log')
            } else if (!item.signature && item.mark_destroy_user) {
                return this.$t('error_cannot_edit_destroyed_log')
            }
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.handleSignLogCancel();
            }
        });
    }
};
</script>
